import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownOptions, FieldControllerTypes, IFormController } from '../../form_v2/types';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import PersonIcon from '@mui/icons-material/Person';
import FeedIcon from '@mui/icons-material/Feed';
import { Form, Formik, FormikContextType } from 'formik';
import FormLayout from '../../layout/Form/FormLayout';
import { VerticalStepper } from '../../Stepper';
import { formInStepperBuilder } from '../../form_v2/helpers';
import FormActionButtonLayout from '../../layout/Form/FormActionButtonLayout';
import { Button, CircularProgress } from '@mui/material';
import { isSubmitEnabled } from '../../../lib/helperFuncs';
import DialogAction from '../../Dialog/DialogAction';
import { useNavigate } from 'react-router-dom';
import { CreateUserValidation } from '../../../lib/static/users/validationSchema';
import { UserFormProps } from './type';
import HttpService from '../../../services/HttpService';
import { User_status, User_status_Dictionary_Caps } from '../../dictionaries/UserStatus';
import AsyncButton from '../../AsyncButton/AsyncButton';


const UserFormSteps = ({initialValues, onSubmit, isLoading, edit=false}: UserFormProps) => {
  const [selectedRole, setSelectedRole] =
    useState<string[]>(initialValues?.roles?.map(role => role.value) || []);
  const [roles, setRoles] = useState<DropdownOptions[]>();
  const [adminUnits, setAdminUnits] = useState<DropdownOptions[]>();
  const [openSubmit, setOpenSubmit] = useState<boolean>(false);
  const [openCancel, setOpenCancel] = useState<boolean>(false);

  const { t } = useTranslation();

  const [isRolesLoading, setIsRolesLoading] = useState<boolean>(false);

  const formRef = useRef<FormikContextType<any>>(null);

  const navigate = useNavigate();

  const filterUnits = (roles: string[]) => {
    
    if (roles.filter(Boolean).length > 0) {      
      const units = [...new Set(roles.filter(Boolean).map(role => {
        //role.split(' ')[0] will return an array containing some of the following: 'EKOME', 'Film', {others}
        return (role?.split(' ')[0] === 'EKOME' && role?.split(' ')[1] !== 'Interoperability')
          ? 'Διεύθυνση'
          : role.split(' ')[0];
      }))];

      const filtered_units = units.some(x => x !== 'Διεύθυνση' && x !== 'Film')
        ? adminUnits
        : adminUnits?.filter(x => units.includes(x.key.split(' ')[0]));
      return filtered_units;
    }
    return [];
  };


  const step1Form: IFormController[] = [
    {
      control: FieldControllerTypes.input,
      label: t('Όνομα'),
      name: 'first_name',
      required: true,
    },
    {
      control: FieldControllerTypes.input,
      label: t('Επώνυμο'),
      name: 'last_name',
      required: true,
    },
    {
      control: FieldControllerTypes.input,
      label: t('ΑΦΜ'),
      name: 'afm',
      required: false
    }
  ];

  const step2Form: IFormController[] = [
    {
      control: FieldControllerTypes.input,
      label: 'Εmail',
      name: 'email',
      required: true,
      disabled: edit
        ? true
        : false,
      info: true,
      tooltip: t('To Email θα είναι και το username του Χρήστη')
    },{
      control: FieldControllerTypes.telInput,
      label: t('Τηλέφωνο'),
      name: 'phone_number',
      required: true,
    }
  ];

  const step3Form: IFormController[] = [
    {
      control: FieldControllerTypes.autocomplete,
      label: t('Ρόλος'),
      name: 'roles',
      options: roles,
      required: true,
      isLoading: isRolesLoading,
      startAdornment: isRolesLoading
        ? <CircularProgress size={20}/>
        : undefined,
      customLabel: true,
      selectedItem: (selectedValue: {key: string, value: string}[]) => setSelectedRole(
        selectedValue.map(role => role.key)
      ),
    },
    {
      control: FieldControllerTypes.autoselect,
      label: t('Οργανική Μονάδα'),
      name: 'administrative_unit_id',
      required: true,
      disabled: selectedRole.length === 0,
      startAdornment: isRolesLoading
        ? <CircularProgress size={20}/>
        : undefined,
      options: filterUnits(selectedRole),
      customLabel: true
    },
    {
      control: FieldControllerTypes.select,
      label: t('Κατάσταση'),
      name: 'is_enabled',
      required: true,
      disabled: true,
      info: true,
      options: edit
        ? [{key: User_status_Dictionary_Caps[initialValues.is_enabled
          ? User_status.Enabled
          : User_status.Disabled],
        value: initialValues.is_enabled}]
        : [{key: 'ΑΝΕΝΕΡΓΟΣ', value: false}],
      tooltip: t('Η Κατάσταση του χρήστη "ΑΝΕΝΕΡΓΟΣ" θα αλλάξει αυτόματα σε "ΕΝΕΡΓΟΣ" μετά την πρώτη σύνδεση του χρήστη στο σύστημα.')
    }
  ];

  const step1 = {
    label: t('Προσωπικά Στοιχεία'),
    content: step1Form,
    icon: <PersonIcon />
  };
  const step2 = {
    label: t('Στοιχεία Επικοινωνίας'),
    content: step2Form,
    icon: <LocalPhoneIcon />
  };
  const step3 = {
    label: t('Χαρακτηριστικά Στελέχους'),
    content: step3Form,
    icon: <FeedIcon />
  };

  const wholeForm = [
    {...step1},
    {...step2},
    {...step3}
  ];
  
  const getStep3Parametrics = async () => {
    setIsRolesLoading(true);
    const responseRoles = await HttpService.getAxiosClient().get(`${process.env.REACT_APP_BACKEND_URL}/users-parametrics/roles`);
    setRoles(responseRoles.data.roles);
    setIsRolesLoading(false);
  };

  const getStep3Units = async () => {
    const responseAdmUnit = await HttpService.getAxiosClient().get(`${process.env.REACT_APP_BACKEND_URL}/users-parametrics/administrative_unit`);    
    setAdminUnits(responseAdmUnit.data.units.sort((a: any, b: any) => a.key.localeCompare(b.key, 'el')));
  };

  useEffect(() => {
    getStep3Units();
    getStep3Parametrics();
  }, []);

  return (
    <>
      <Formik
        onSubmit={() => undefined}
        initialValues={initialValues}
        validationSchema={CreateUserValidation}
        validateOnBlur={false}
        innerRef={formRef}
      >
        {formik =>
          (<div>
            <Form>
              <FormLayout>
                <VerticalStepper activeStep={1}>
                  {formInStepperBuilder(wholeForm, formik)}
                </VerticalStepper>
              </FormLayout>
              <FormActionButtonLayout>
                <Button variant='outlined' color='error'
                  onClick={() => setOpenCancel(true)}> Ακύρωση </Button>
                <AsyncButton 
                  title={edit
                    ? t('Αποθήκευση')
                    : t('Υποβολή')}
                  isLoading={isLoading}
                  onClick={() => setOpenSubmit(true)}
                  disabled={isSubmitEnabled(formik?.errors, formik?.dirty)}
                />
                <DialogAction open={openCancel} handleClose={() => setOpenCancel(false)}
                  title={String(t('Ακύρωση Δημιουργίας Χρήστη'))} 
                  content={edit
                    ? t('Είστε σίγουρος/η ότι θέλετε να ακυρώσετε την επεξεργασία του χρήστη;')
                    : t('Ειστε σιγουρος/η ότι θέλετε να ακυρώσετε την δημιουργία χρήστη;')
                  }
                  onAccept={() => {
                    setOpenCancel(false); navigate(-1);
                  }}/>
                <DialogAction open={openSubmit} handleClose={() => setOpenSubmit(false)}
                  title={edit 
                    ? String(t('Επιβεβαίωση Επεξεργασίας'))
                    : String(t('Επιβεβαίωση Υποβολής'))
                  } 
                  content={edit
                    ? t('Είστε σίγουρος/η ότι θέλετε να προχωρήσετε με την επεξεργασία του Χρήστη;')
                    : t('Είστε σίγουρος/η ότι θέλετε να προχωρήσετε με την δημιουργία του Χρήστη;')}
                  onAccept={() => {
                    onSubmit(formik.values);
                    setOpenSubmit(false);
                  }}/>
              </FormActionButtonLayout>
            </Form>
          </div>)
        }
      </Formik>
    </>
  );
};

export default UserFormSteps;